/**
 * InvestigationCaseFile — ONE case-file detail view (stepper, findings and
 * remediation editors, FSM transition buttons derived only from the server's
 * allowed_transitions), shared by every surface that shows an INV case:
 *
 *  - the thread/case workspace's detail pane (embedded mode: the pane
 *    already shows the item's title and sits beside the conversation, so
 *    those are elided), and
 *  - the CaseFileModal pop-out (embedded + popped: the modal's own head
 *    carries the identity row; the sections flow into columns).
 *
 * Full mode (title, context, links out to the conversation) is kept for a
 * host that shows the case on its own.
 *
 * Extracted from the retired InvestigationsPanel so the door's "Open
 * investigation" lands on the real case controls instead of a dead end
 * (Scott, 2026-08-20 demo: the approved action didn't visibly produce the
 * case). Styles ride InvestigationCaseFile.module.css — the one case-file
 * stylesheet — so every host renders the case identically.
 */
import { useEffect, useState } from 'react';

import {
  addInvestigationNote,
  addInvestigationTask,
  editInvestigationTask,
  getInvestigationHistory,
  listInvestigationNotes,
  getPackagingFeedbackExport,
  recordCustomerResponse,
  setInvestigationBridgeKeys,
  setInvestigationWorkState,
  setInvestigationWait,
  endInvestigationWait,
  transitionInvestigation,
  transitionInvestigationTask,
  type CaseTaskOwner,
  type CaseWaitParty,
  type CaseWorkState,
  type DoorCaseTaskRead,
  type DoorInvestigationRead,
  type DoorPackagingFeedbackRead,
  type InvestigationTransitionBody,
} from '../../services/qcpInvestigations';
import {
  choicesHint,
  copyToClipboard,
  sectionClipboardText,
  sourceHint,
} from './packagingFeedbackExport';
import {
  STATUS_LABEL,
  STEPPER_STEPS,
  actorWord,
  agingInfo,
  complaintFieldRows,
  conversationsLabel,
  formatInvestigationDate,
  externalClosureNotice,
  failedActionMessage,
  linkedIssues,
  isActiveCase,
  linkedConversationMeta,
  closePathLine,
  linkedConversationRows,
  openAsksLabel,
  recurredLabel,
  resurrectionChipSpecs,
  showLinkedConversations,
  splitRemediationLines,
  stepperIndex,
  taskToggleTarget,
  transitionButtonSpecs,
  unseenResponseLabel,
  visibleTasks,
  type TransitionButtonSpec,
} from './investigationsView';
import { keyLabel } from './keyLabel';
import { courtLine, courtReasonLine, customerItemsLine, expectedWord, unaskedItemsLine } from './caseCourtView';
import { caseEventSummary } from './caseHistoryView';
import { NoteBox } from './NoteBox';
import { useHistoryPages } from './useHistoryPages';
import {
  TASK_OWNER_LABEL,
  nextTaskOwner,
  taskOwnerAction,
  taskOwnerHint,
} from './taskOwner';
import { pillLabel } from '../../pages/quality-triage/merlinBrief';
import { primaryRef } from '../../pages/quality-triage/caseIdentity';
import { AlsoKnownAsLine } from '../../pages/quality-triage/AlsoKnownAsLine';
import { trackEvent } from '../../services/portalTracking';
import { DismissControl } from '../quality/DismissControl';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { STATUS_PILL_CLASS, collapseRepeats, isTechnicalKeyValue } from './caseFileView';
import { taskAskDesk, taskAskGroups, taskCanAsk } from './caseTaskAsks';
import {
  CUSTOMER_STEP_HINT,
  customerRecommendation,
  defaultCustomerRecommendation,
  hasOwnRecommendation,
  isCustomerStep,
} from './customerStep';
import { useCustomerRelay } from '../quality/useCustomerRelay';
import CaseSlaClocks from './CaseSlaClocks';
import {
  customerResponseLine,
  taskAssignmentLine,
  taskClosedFromChatLine,
} from './slaClocksView';
import styles from './InvestigationCaseFile.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface InvestigationCaseFileProps {
  investigation: DoorInvestigationRead;
  onUpdated: (updated: DoorInvestigationRead) => void;
  /** Board embed: skip the title/context the host already shows. */
  embedded?: boolean;
  /** Popped out into CaseFileModal: lay the sections out in columns and pin
   *  the case actions, since the width is finally there for it. */
  popped?: boolean;
  /** Jump to the triage board / conversation behind this case, when a thread
   *  links here. Scott, using it: "there's remediation actions, but it
   *  doesn't take me to the conversation. I don't see how to get there." */
  onOpenConversation?: () => void;
  /** Fallback for case files saved straight from chat (no thread): load the
   *  originating conversation in the main chat instead of dead-ending. */
  onContinueInChat?: () => void;
  /** Open asks the host lists under "Asks on this case" — the header badge
   *  counts the same list (it can include asks that only name the case). */
  openAskCount?: number;
  /** The conversation the host is already showing beside this case (the
   *  workspace pane / the thread's own conversation). Its row reads as the
   *  one on screen, and a case whose only conversation is that one lists
   *  nothing — the rows exist to reach the OTHER conversations. */
  activeConversationId?: string | null;
  /** "Open" on a linked-conversation row: show THAT conversation (approved
   *  mockup: one row per conversation, Open jumps to it). Hosts that cannot
   *  switch conversations leave it unset and the rows list without a button. */
  onOpenLinkedConversation?: (conversationId: string) => void;
  /** The asks the host lists under "Asks on this case": the ones sent from
   *  an outstanding item show on that item's row as a pill (kanban 52a4147a). */
  asks?: ReadonlyArray<DoorDataRequestRead>;
  /** "Ask <desk>" on an outstanding item's row: open the host's Ask modal for
   *  it. Jess, on INV-00006: the trigger's Ask buttons were gone once the case
   *  was opened; she wanted one beside the item ("clear SHERW01 credit hold",
   *  to Laura). Hosts with nowhere to send from leave it unset. */
  onAskTask?: (task: DoorCaseTaskRead) => void;
  /** The desk a row's Ask names when its own label names nobody, by task
   *  id — the host reads it off the Do next step the item restates. */
  taskDesks?: Record<string, string>;
}

/** The unsaved findings draft for a case (localStorage; may be unavailable). */
function readFindingsDraft(id: string): string | null {
  try {
    return window.localStorage.getItem(`qcp-findings-draft:${id}`);
  } catch {
    return null;
  }
}

function writeFindingsDraft(id: string, text: string | null): void {
  try {
    if (text === null) window.localStorage.removeItem(`qcp-findings-draft:${id}`);
    else window.localStorage.setItem(`qcp-findings-draft:${id}`, text);
  } catch {
    /* storage off: the draft lives only on screen, as before */
  }
}

/** Complaint text longer than this folds to its first lines. */
const COMPLAINT_FOLD_CHARS = 280;

/** Rows for a text box so its content reads without an inner scroll (4–18). */
function textRows(text: string, charsPerLine = 90): number {
  const lines = text
    .split('\n')
    .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / charsPerLine)), 0);
  return Math.min(18, Math.max(4, lines + 1));
}

/**
 * Where a failed change shows its error: beside the control that made it
 * (fan-out finding 2: removing a key, flipping an item's desk or moving the
 * stage failed with nothing to see, the one error line sat far below).
 */
type FailSpot = 'keys' | 'court' | 'wait' | 'addTask' | 'stage' | 'customerResponse' | `task:${string}`;

/** The red line under a control whose change the server refused. */
function FailNote({ message }: { message: string }) {
  return (
    <div className={styles.transitionError} role="alert" data-testid="case-action-error">
      {message}
    </div>
  );
}

/** A key or item name in quotes for an error sentence; a long checklist
 *  item is cut to its start so the sentence stays readable. */
function quoted(text: string, max = 60): string {
  let short = text;
  if (text.length > max) {
    const cut = text.slice(0, max - 1);
    const space = cut.lastIndexOf(' ');
    short = `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}\u2026`;
  }
  return `\u201c${short}\u201d`;
}

export function InvestigationCaseFile({
  investigation,
  onUpdated,
  embedded = false,
  popped = false,
  onOpenConversation,
  onContinueInChat,
  openAskCount,
  activeConversationId,
  onOpenLinkedConversation,
  asks = [],
  onAskTask,
  taskDesks,
}: InvestigationCaseFileProps) {
  // Keyed by investigation.id at the call site, so drafts reset per case.
  // Findings save with the next stage move, so a draft is kept on this
  // computer until then (turn 2: "no Save button, not sure my text was kept").
  const [findingsDraft, setFindingsDraftState] = useState(
    () => readFindingsDraft(investigation.id) ?? investigation.findings,
  );
  const setFindingsDraft = (text: string) => {
    setFindingsDraftState(text);
    writeFindingsDraft(investigation.id, text === investigation.findings ? null : text);
  };
    const [remediationDraft, setRemediationDraft] = useState(
    investigation.remediation_details.join('\n'),
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ spot: FailSpot; message: string } | null>(null);
  const failedAt = (spot: FailSpot) =>
    failure?.spot === spot ? <FailNote message={failure.message} /> : null;
  // "Packaging feedback": the case rendered into the sales-site complaint
  // form's sections for copy-and-paste (Jess, QT-10017 / QT-10012). Loaded on
  // demand — it is an output step, not something every case view needs.
  const [feedback, setFeedback] = useState<DoorPackagingFeedbackRead | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  useEffect(() => {
    setFeedback(null);
    setFeedbackError(null);
    setCopiedKey(null);
  }, [investigation.id]);
  const loadFeedback = async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      setFeedback(await getPackagingFeedbackExport(investigation.id));
    } catch {
      setFeedbackError('Could not prepare the packaging feedback. Try again.');
    } finally {
      setFeedbackLoading(false);
    }
  };
  const copySection = async (key: string, text: string) => {
    const ok = await copyToClipboard(text);
    setCopiedKey(ok ? key : null);
    setFeedbackError(ok ? null : 'Copy failed. Select the text and copy it by hand.');
  };
  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [openComplaints, setOpenComplaints] = useState<Set<string>>(() => new Set());
  // The removed items fold (kanban 59a1ee04): what Jess cleared, with his
  // reason, never deleted.
  const [showDismissed, setShowDismissed] = useState(false);
  const [newTaskOwner, setNewTaskOwner] = useState<CaseTaskOwner>('us');
  // Sigma stage 2 on a new item: who, by name, and by when (both optional).
  const [newTaskOwnerName, setNewTaskOwnerName] = useState('');
  const [newTaskDueAt, setNewTaskDueAt] = useState('');
  // The item whose owner / date is being edited inline, and the draft.
  const [assigningTask, setAssigningTask] = useState<string | null>(null);
  const [assignName, setAssignName] = useState('');
  const [assignDate, setAssignDate] = useState('');
  // A customer-owed step's recommendation being reworded (kanban d3915b86).
  const [recommendingTask, setRecommendingTask] = useState<string | null>(null);
  const [recommendDraft, setRecommendDraft] = useState('');
  // The account's sales rep the recommendation pre-names; fetched only while
  // an open customer step is on a live case.
  const customerRelay = useCustomerRelay(
    isActiveCase(investigation) &&
      investigation.tasks.some((t) => isCustomerStep(t) && t.status === 'open')
      ? { kind: 'case', id: investigation.id }
      : null,
  );
  // Sigma stage 4: what the customer was told, before "Mark as sent".
  const [customerResponseDraft, setCustomerResponseDraft] = useState('');
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  // "We're waiting on <who> for <what>, expected <date>" (kanban 88f93c9b):
  // the fact the court follows, typed on the case instead of told to Merlin.
  const [waitOpen, setWaitOpen] = useState(false);
  const [waitParty, setWaitParty] = useState<CaseWaitParty>('customer');
  const [waitWho, setWaitWho] = useState('');
  const [waitWhat, setWaitWhat] = useState('');
  const [waitVia, setWaitVia] = useState('');
  const [waitExpected, setWaitExpected] = useState('');
  const [waitReason, setWaitReason] = useState('');
  // The case's audit-trail history, newest first a page at a time. null =
  // loading; 'error' hides the section (no timeline is quieter than a broken
  // one — the thread board's rule). Refetched when the case changes
  // (updated_at) so new events show; "Show older" pages back.
  const history = useHistoryPages(
    (q) => getInvestigationHistory(investigation.id, q),
    investigation.id,
    investigation.updated_at,
  );
  const historyWindow = history.window !== null && history.window !== 'error' ? history.window : null;

  const stepIndex = stepperIndex(investigation.status);
  const specs = transitionButtonSpecs(
    investigation.status,
    investigation.allowed_transitions,
  );
  // Findings are saved by the move to Remediating, so they are only editable
  // before it: an edit made while Remediating was silently dropped (turn 4).
  // A read-only viewer (kanban 808dabf6): the file reads the same, every
  // edit control (keys, waits, items, stages, the customer response) is gone.
  const readOnly = useQualityReadOnly();
  // A viewer reads the findings and actions on the case; the drafts are the desk's.
  const findingsEditable = investigation.status === 'investigating' && !readOnly;
  const remediationEditable = investigation.status === 'remediating' && !readOnly;
  const aging = agingInfo(investigation.oldest_open_request_at);
  const openAsks = openAsksLabel(openAskCount ?? investigation.open_request_count);
  const unseen = unseenResponseLabel(investigation.unseen_response_count);
  const convos = conversationsLabel(investigation.conversation_ids.length);
  const conversationRows = linkedConversationRows(investigation);
  const listConversations = showLinkedConversations(
    conversationRows,
    activeConversationId,
  );
  const recurred = recurredLabel(investigation.reopened_count);
  const resurrectionChips = resurrectionChipSpecs(investigation);
  const keyEntries = Object.entries(investigation.bridge_keys ?? {}).sort(
    ([a], [b]) => a.localeCompare(b),
  );
  const complaints = linkedIssues(investigation);
  const stillOpenElsewhere = externalClosureNotice(investigation);

  // A transition that needs a word first (a reopen note, a cancel, or a
  // complete with items still open) opens an on-page step instead of a
  // native browser dialog: turn 3's Reopen "flashed and vanished" because a
  // window.prompt never shows to anything but a person at the tab.
  const [pending, setPending] = useState<TransitionButtonSpec | null>(null);
  const [reopenNote, setReopenNote] = useState('');
  const openItemCount = investigation.tasks.filter((t) => t.status === 'open').length;

  // Keys are how Done finds a case later (turn 5 completed one with none).
  const noKeys = Object.keys(investigation.bridge_keys ?? {}).length === 0;
  const needsStep = (spec: TransitionButtonSpec) =>
    spec.confirm ||
    spec.needs === 'reopen_note' ||
    (spec.to === 'completed' && (openItemCount > 0 || noKeys));

  const stageAction = (spec: TransitionButtonSpec) =>
    spec.to === 'cancelled'
      ? `cancel ${investigation.investigation_no}`
      : spec.needs === 'reopen_note'
        ? `reopen ${investigation.investigation_no}`
        : `move ${investigation.investigation_no} to ${STATUS_LABEL[spec.to]}`;

  const run = async (spec: TransitionButtonSpec, confirmed = false) => {
    if (busy) return;
    setFailure(null);
    if (!confirmed && needsStep(spec)) {
      setPending(spec);
      setReopenNote('');
      return;
    }
    const body: InvestigationTransitionBody = { to_status: spec.to };
    if (spec.needs === 'findings') body.findings = findingsDraft.trim();
    if (spec.needs === 'remediation_details') {
      body.remediation_details = splitRemediationLines(remediationDraft);
    }
    if (spec.needs === 'reopen_note' && reopenNote.trim()) {
      body.reopen_note = reopenNote.trim();
    }
    setPending(null);
    setBusy(true);
    try {
      const updated = await transitionInvestigation(investigation.id, body);
      writeFindingsDraft(investigation.id, null);
      onUpdated(updated);
    } catch (e) {
      // 400 = FSM gate refusal (e.g. findings required) — show the detail.
      setFailure({ spot: 'stage', message: failedActionMessage(stageAction(spec), e) });
    } finally {
      setBusy(false);
    }
  };

  // Work-state flips, key and checklist writes share the busy plumbing —
  // every action folds the server's full DTO back in. A failure is filed
  // under the control that made it and names what did not happen; starting
  // any other change clears it.
  const runCaseAction = async (
    spot: FailSpot,
    what: string,
    action: () => Promise<DoorInvestigationRead>,
  ) => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      onUpdated(await action());
    } catch (e) {
      setFailure({ spot, message: failedActionMessage(what, e) });
    } finally {
      setBusy(false);
    }
  };

  // Remove a checklist item (kanban 59a1ee04): the control records the press
  // and shows a refusal, so this only has to land it or throw.
  const removeTask = async (task: DoorCaseTaskRead, note: string | null) => {
    if (busy) throw new Error('busy');
    setBusy(true);
    setFailure(null);
    try {
      onUpdated(await transitionInvestigationTask(investigation.id, task.id, 'cancelled', { note }));
    } catch (e) {
      setFailure({ spot: `task:${task.id}`, message: failedActionMessage(`remove ${quoted(task.label)}`, e) });
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const saveWait = () => {
    const who = waitWho.trim();
    const what = waitWhat.trim();
    if (!who || !what) return;
    trackEvent('action', 'qcp_case_wait_set', {
      investigation_no: investigation.investigation_no,
      investigation_id: investigation.id,
      party: waitParty,
      has_expected_on: Boolean(waitExpected),
    });
    void runCaseAction('wait', `record that the case waits on ${quoted(who)}`, async () => {
      const updated = await setInvestigationWait(investigation.id, {
        party: waitParty,
        who_name: who,
        what_for: what,
        via_name: waitVia.trim() || null,
        expected_on: waitExpected || null,
        reason: waitReason.trim() || null,
      });
      setWaitOpen(false);
      setWaitWho('');
      setWaitWhat('');
      setWaitVia('');
      setWaitExpected('');
      setWaitReason('');
      return updated;
    });
  };

  const endWait = (outcome: 'received' | 'cleared') => {
    trackEvent('action', 'qcp_case_wait_end', {
      investigation_no: investigation.investigation_no,
      investigation_id: investigation.id,
      outcome,
    });
    void runCaseAction(
      'wait',
      outcome === 'received' ? 'record that it arrived' : 'stop waiting',
      () => endInvestigationWait(investigation.id, outcome),
    );
  };

  const addTask = () => {
    const label = newTaskLabel.trim();
    if (!label) return;
    void runCaseAction('addTask', `add the step ${quoted(label)}`, async () => {
      const updated = await addInvestigationTask(investigation.id, label, newTaskOwner, {
        owner_name: newTaskOwnerName.trim() || null,
        due_at: newTaskDueAt || null,
      });
      setNewTaskLabel('');
      setNewTaskOwnerName('');
      setNewTaskDueAt('');
      return updated;
    });
  };

  const openAssign = (task: DoorCaseTaskRead) => {
    setFailure(null);
    setAssigningTask(task.id);
    setAssignName(task.owner_name ?? '');
    setAssignDate(task.due_at ?? '');
  };

  const saveAssign = (task: DoorCaseTaskRead) => {
    const name = assignName.trim();
    void runCaseAction(`task:${task.id}`, `set who and when on ${quoted(task.label)}`, async () => {
      const updated = await editInvestigationTask(investigation.id, task.id, {
        ...(name ? { owner_name: name } : { clear_owner_name: true }),
        ...(assignDate ? { due_at: assignDate } : { clear_due_at: true }),
      });
      setAssigningTask(null);
      return updated;
    });
  };

  const openRecommend = (task: DoorCaseTaskRead) => {
    setFailure(null);
    setRecommendingTask(task.id);
    setRecommendDraft(task.recommendation ?? '');
  };

  // Jess's wording of how the step reaches the customer; blank goes back to
  // the default ("through <the account's sales rep>"). Nothing is sent.
  const saveRecommend = (task: DoorCaseTaskRead) => {
    const text = recommendDraft.trim();
    void runCaseAction(`task:${task.id}`, `reword the recommendation on ${quoted(task.label)}`, async () => {
      const updated = await editInvestigationTask(
        investigation.id,
        task.id,
        text ? { recommendation: text } : { clear_recommendation: true },
      );
      setRecommendingTask(null);
      return updated;
    });
  };

  const markCustomerResponseSent = () => {
    const summary = customerResponseDraft.trim();
    if (!summary) return;
    void runCaseAction('customerResponse', 'record the customer response', async () => {
      const updated = await recordCustomerResponse(investigation.id, summary);
      setCustomerResponseDraft('');
      return updated;
    });
  };

  const addKey = () => {
    const key = newKeyName.trim();
    const value = newKeyValue.trim();
    if (!key || !value) return;
    void runCaseAction('keys', `add the key ${quoted(keyLabel(key))}`, async () => {
      const updated = await setInvestigationBridgeKeys(investigation.id, {
        ...investigation.bridge_keys,
        [key]: value,
      });
      setNewKeyName('');
      setNewKeyValue('');
      return updated;
    });
  };

  const active = isActiveCase(investigation);
  const canEdit = active && !readOnly;
  const checklist = visibleTasks(investigation.tasks);
  const dismissedTasks = investigation.tasks.filter((t) => t.status === 'cancelled');

  const buttonClass = (kind: TransitionButtonSpec['kind']) =>
    `${styles.transitionBtn} ${
      kind === 'primary'
        ? styles.transitionPrimary
        : kind === 'danger'
          ? styles.transitionDanger
          : styles.transitionGhost
    }`;

  return (
    <div
      className={styles.detail}
      data-embedded={embedded || undefined}
      data-popped={popped || undefined}
    >
      <div className={styles.detailTop}>
        <span className={styles.cardNo}>{investigation.investigation_no}</span>
        <span
          className={`${styles.statusPill} ${STATUS_PILL_CLASS[investigation.status]}`}
        >
          {STATUS_LABEL[investigation.status]}
        </span>
        {recurred && <span className={styles.badgeRecurred}>{recurred}</span>}
        {resurrectionChips.map((chip) => (
          <span
            key={chip.key}
            className={
              chip.kind === 'resurrects'
                ? styles.badgeResurrects
                : styles.badgeRediscovered
            }
            title={chip.title}
          >
            {chip.label}
          </span>
        ))}
      </div>
      {/* One number per case (proposal 6). Embedded in a thread workspace
          (or popped out), the host header prints this line, so it prints
          once. */}
      {!embedded && <AlsoKnownAsLine identity={primaryRef(null, investigation)} />}

      {/* People's notes lead the case (notes round): a line from a phone
          call, under its writer's name, that Merlin never rewrites. */}
      <NoteBox
        recordId={investigation.id}
        noun="case"
        load={() => listInvestigationNotes(investigation.id)}
        save={(text) => addInvestigationNote(investigation.id, text)}
      />

      {stepIndex !== null && (
        <ol className={styles.stepper} aria-label="Investigation progress">
          {STEPPER_STEPS.map((step, i) => (
            <li
              key={step}
              className={styles.step}
              data-state={
                i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo'
              }
              aria-current={i === stepIndex ? 'step' : undefined}
            >
              <span className={styles.stepDot} aria-hidden="true">
                {i < stepIndex || (i === 2 && stepIndex === 2) ? '✓' : i + 1}
              </span>
              <span className={styles.stepLabel}>{step}</span>
            </li>
          ))}
        </ol>
      )}

      {/* The Sigma "Quality of Complaints" clocks (Jess, QT-10056): where
          each stage stands against its deadline. Server-computed. */}
      <CaseSlaClocks investigation={investigation} onUpdated={onUpdated} />

      {/* One way to move the stage (UX round 2026-11-03, merge): the stage
          buttons at the bottom of the case. The "Next step: … ↓" pill that
          only jumped to them read as a label and pushed toward closing a
          case whose fix was not decided; the stepper above says where the
          case stands. */}

      {!embedded && (
        <h3 className={styles.detailTitle}>{investigation.title}</h3>
      )}
      {onOpenConversation && (
        <button
          type="button"
          className={styles.openConversationBtn}
          onClick={onOpenConversation}
        >
          💬 Open the conversation on the triage board
        </button>
      )}
      {!onOpenConversation && onContinueInChat && (
        <button
          type="button"
          className={styles.openConversationBtn}
          onClick={onContinueInChat}
        >
          💬 Continue this conversation in chat
        </button>
      )}
      {!embedded && investigation.context_summary && (
        <p className={styles.detailContext}>{investigation.context_summary}</p>
      )}

      <div className={styles.detailMeta}>
        <span className={styles.detailCreated}>
          Opened {formatInvestigationDate(investigation.created_at)}
        </span>
        {openAsks && (
          <button
            type="button"
            className={styles.badgeAsks}
            style={{ border: 0, cursor: 'pointer' }}
            title="Show the questions sent out on this case"
            onClick={() =>
              document.getElementById('case-asks')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            {openAsks} · show ↓
          </button>
        )}
        {aging.tier !== 'none' && (
          <span className={styles.badgeWaiting} data-tier={aging.tier}>
            ⏰ {aging.label}
          </span>
        )}
        {unseen && <span className={styles.badgeNew}>{unseen}</span>}
        {/* Plain text, not a pill: it looked clickable and did nothing
            (fan-out finding 9); the conversation is the pane beside this. */}
        {convos && <span className={styles.metaNote}>{convos}</span>}
      </div>

      {listConversations && (
        <section className={styles.section} aria-label="Linked conversations">
          <h4 className={styles.sectionHead}>Linked conversations</h4>
          <ul className={styles.linkList} aria-label="Linked conversations">
            {conversationRows.map((row) => {
              const showing = row.conversationId === activeConversationId;
              return (
                <li
                  key={row.conversationId}
                  className={styles.linkRow}
                  data-originating={row.isOriginating || undefined}
                  data-showing={showing || undefined}
                >
                  <span className={styles.linkIcon} aria-hidden="true">
                    💬
                  </span>
                  <span className={styles.linkText}>
                    <span className={styles.linkHeadline}>{row.title}</span>
                    <span className={styles.linkMeta}>
                      {showing
                        ? 'Showing beside this case'
                        : linkedConversationMeta(row)}
                    </span>
                  </span>
                  {!showing && onOpenLinkedConversation && (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      aria-label={`Open conversation: ${row.title}`}
                      onClick={() => onOpenLinkedConversation(row.conversationId)}
                    >
                      Open
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Owner ruling (Scott, review bz2bb6, 2026-10-04): "The sales site is
          the complaint." The record of the complaint is the sales-site form;
          this case is the investigation of it. Every explorer asked which one
          counted, and the screen used to imply it was the case. */}
      <section className={styles.section} aria-label="The complaint">
        <h4 className={styles.sectionHead}>
          {complaints.length > 1 ? 'The complaints' : 'The complaint'}
        </h4>
        {complaints.length === 0 && (
          <p className={styles.hint} data-testid="no-sales-site-complaint">
            Nothing on the sales site for this one yet, so the complaint itself is
            not on the record — only this investigation. Someone has to raise it
            on the sales site.
          </p>
        )}
          {complaints.map((issue) => {
            const rows = complaintFieldRows(issue);
            return (
              <article
                key={issue.id}
                className={styles.complaintCard}
                data-link-source={issue.link_source}
              >
                <div className={styles.complaintHead}>
                  <span className={styles.cardNo}>{issue.issue_no}</span>
                  <span className={styles.complaintTitle}>{issue.title}</span>
                  {issue.occurred_at && (
                    <span className={styles.complaintDate}>
                      {formatInvestigationDate(issue.occurred_at)}
                    </span>
                  )}
                </div>
                {issue.description && (
                  <>
                    {/* Long sales-site text pushed the checklist screens down
                        (turn 1); the first lines show, the rest on request. */}
                    <p
                      className={styles.complaintText}
                      data-clamped={
                        issue.description.length > COMPLAINT_FOLD_CHARS &&
                        !openComplaints.has(issue.id)
                          ? true
                          : undefined
                      }
                    >
                      {issue.description}
                    </p>
                    {issue.description.length > COMPLAINT_FOLD_CHARS && (
                      <button
                        type="button"
                        className={styles.complaintMore}
                        aria-expanded={openComplaints.has(issue.id)}
                        onClick={() =>
                          setOpenComplaints((prev) => {
                            const next = new Set(prev);
                            if (next.has(issue.id)) next.delete(issue.id);
                            else next.add(issue.id);
                            return next;
                          })
                        }
                      >
                        {openComplaints.has(issue.id) ? 'Show less' : 'Show the whole complaint'}
                      </button>
                    )}
                  </>
                )}
                {rows.length > 0 && (
                  <dl className={styles.complaintGrid}>
                    {rows.map((row) => (
                      <div key={row.label} className={styles.complaintField}>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <p className={styles.hint}>
                  {issue.link_source === 'thread'
                    ? 'Cited by the thread this case was opened from.'
                    : 'Matched from this case\u2019s keys.'}{' '}
                  The complaint of record lives on the sales site; this is a
                  read-only copy, and this case is the investigation of it.
                </p>
              </article>
            );
          })}
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionHead}>Case keys</h4>
        {keyEntries.length === 0 && (
          <p className={styles.hint}>
            {active
              ? 'No keys yet. Tag the customer, product, lot or order this ' +
                'case is about so it can be found later from the Done search.'
              : 'No keys were tagged on this case.'}
          </p>
        )}
        {keyEntries.length > 0 && (
          <ul className={styles.keyChips} aria-label="Case keys">
            {keyEntries
              // Internal ids link records; nobody reads them (turn 2 removed the
              // "Show internal ids" toggle).
              .filter(([, value]) => !isTechnicalKeyValue(String(value)))
              .map(([key, value]) => (
              <li key={key} className={styles.keyChip}>
                <span className={styles.keyChipName} title={key}>
                  {keyLabel(key)}
                </span>
                <span className={styles.keyChipValue}>{value}</span>
                {canEdit && (
                  <button
                    type="button"
                    className={styles.keyChipRemove}
                    aria-label={`Remove key ${key}`}
                    disabled={busy}
                    onClick={() => {
                      const next = { ...investigation.bridge_keys };
                      delete next[key];
                      void runCaseAction(
                        'keys',
                        `remove the key ${quoted(keyLabel(key))}`,
                        () => setInvestigationBridgeKeys(investigation.id, next),
                      );
                    }}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <div className={styles.keyAddRow}>
            <input
              className={styles.keyInput}
              value={newKeyName}
              list="case-key-names"
              placeholder="customer_no, sku, lot…"
              aria-label="New key name"
              disabled={busy}
              onChange={(e) => setNewKeyName(e.target.value)}
            />
            <datalist id="case-key-names">
              <option value="customer_no" />
              <option value="sku" />
              <option value="lot" />
              <option value="order_no" />
              <option value="batch" />
            </datalist>
            <input
              className={styles.keyInput}
              value={newKeyValue}
              placeholder="value"
              aria-label="New key value"
              disabled={busy}
              onChange={(e) => setNewKeyValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addKey();
              }}
            />
            <button
              type="button"
              className={styles.taskAddBtn}
              disabled={busy || !newKeyName.trim() || !newKeyValue.trim()}
              onClick={addKey}
            >
              Add key
            </button>
          </div>
        )}
        {failedAt('keys')}
      </section>

      {active && (
        <section className={styles.section}>
          <h4 className={styles.sectionHead}>Whose court is it in?</h4>
          {/* Proposal 5: the court follows the asks (the server's `court`);
              only the sign-off is set by hand. The stored work_state history
              stays on the timeline. */}
          <p className={styles.courtNow} data-testid="case-file-court">
            {courtLine(investigation.court)}
          </p>
          {courtReasonLine(investigation.court) && (
            <p className={styles.courtReason} data-testid="case-file-court-reason">
              {courtReasonLine(investigation.court)}
            </p>
          )}
          {investigation.wait && investigation.wait.state === 'waiting' && (
            <p className={styles.courtMeta} data-testid="case-file-wait">
              Waiting on {investigation.wait.who_name} for {investigation.wait.what_for}
              {investigation.wait.via_name ? `; ${investigation.wait.via_name} asked them` : ''}
              {investigation.wait.told_by ? ` · told by ${investigation.wait.told_by}` : ''}
              {investigation.wait.followup_id && investigation.wait.expected_on
                ? ` · Merlin checks back ${(expectedWord(investigation.court) ?? '').replace('expected ', 'on ')}`
                : ''}
            </p>
          )}
          {unaskedItemsLine(investigation) && (
            <p className={styles.courtUnasked} data-testid="case-file-court-unasked">
              {unaskedItemsLine(investigation)}: use its Ask button, or say below what the case
              waits on.
            </p>
          )}
          {!readOnly && customerItemsLine(investigation) && (
            // The customer is reached through the sales rep, never asked
            // from here (kanban d3915b86): point at the recommendation.
            <p className={styles.courtUnasked} data-testid="case-file-court-customer">
              {customerItemsLine(investigation)}: the account&rsquo;s sales rep reaches them (the
              item&rsquo;s recommendation says how); once they are asked, say below what the case
              waits on.
            </p>
          )}
          {!readOnly && (<p className={styles.courtHint}>
            Follows what the case waits on and its open asks. Tell Merlin, or say it here, and the
            case moves itself.
          </p>)}
          {!readOnly && (investigation.wait && investigation.wait.state === 'waiting' ? (
            <div className={styles.workStateOptions}>
              <button
                type="button"
                className={styles.workStateOption}
                disabled={busy}
                onClick={() => endWait('received')}
                data-testid="case-file-wait-received"
              >
                ✓ It arrived
              </button>
              <button
                type="button"
                className={styles.workStateOption}
                disabled={busy}
                onClick={() => endWait('cleared')}
                data-testid="case-file-wait-cleared"
              >
                Not waiting after all
              </button>
            </div>
          ) : (
            <>
              {!waitOpen && (
                <button
                  type="button"
                  className={styles.workStateOption}
                  disabled={busy}
                  onClick={() => setWaitOpen(true)}
                  data-testid="case-file-wait-open"
                >
                  ⏳ Waiting on someone…
                </button>
              )}
              {waitOpen && (
                <div className={styles.waitForm} data-testid="case-file-wait-form">
                  <div className={styles.taskAddRow}>
                    <select
                      className={styles.taskOwnerSelect}
                      value={waitParty}
                      onChange={(e) => setWaitParty(e.target.value as CaseWaitParty)}
                      aria-label="Waiting on whom"
                      disabled={busy}
                    >
                      <option value="customer">the customer</option>
                      <option value="colleague">a colleague</option>
                    </select>
                    <input
                      className={styles.textInput}
                      value={waitWho}
                      onChange={(e) => setWaitWho(e.target.value)}
                      placeholder="Who (Hartz, Jake Butchko)"
                      aria-label="Who the case waits on"
                      disabled={busy}
                    />
                    <input
                      className={styles.textInput}
                      value={waitWhat}
                      onChange={(e) => setWaitWhat(e.target.value)}
                      placeholder="For what (retained samples)"
                      aria-label="What the case waits for"
                      disabled={busy}
                    />
                  </div>
                  <div className={styles.taskAddRow}>
                    <input
                      className={`${styles.textInput} ${styles.taskOwnerName}`}
                      value={waitVia}
                      onChange={(e) => setWaitVia(e.target.value)}
                      placeholder="Who asked them (optional)"
                      aria-label="Who asked them"
                      disabled={busy}
                    />
                    <input
                      type="date"
                      className={styles.taskDateInput}
                      value={waitExpected}
                      onChange={(e) => setWaitExpected(e.target.value)}
                      aria-label="Expected on"
                      title="Merlin checks back that morning and brings the case up"
                      disabled={busy}
                    />
                    <input
                      className={styles.textInput}
                      value={waitReason}
                      onChange={(e) => setWaitReason(e.target.value)}
                      placeholder="One line for the card (optional)"
                      aria-label="Reason shown on the card"
                      disabled={busy}
                    />
                    <button
                      type="button"
                      className={styles.taskAddBtn}
                      onClick={saveWait}
                      disabled={busy || !waitWho.trim() || !waitWhat.trim()}
                      data-testid="case-file-wait-save"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.workStateOption}
                      onClick={() => setWaitOpen(false)}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          ))}
          {failedAt('wait')}
          {!readOnly && (() => {
            const ready = investigation.work_state === 'ready_to_close';
            const to: CaseWorkState = ready ? 'outstanding_on_us' : 'ready_to_close';
            const allowed = investigation.allowed_work_states.includes(to);
            const closePath = ready ? closePathLine(investigation.status) : null;
            return (
              <>
                {ready && (
                  <span className={styles.readyState} data-testid="case-ready-state">
                    🏁 Ready to close
                  </span>
                )}
                <button
                  type="button"
                  className={styles.workStateOption}
                  data-work-state={ready ? undefined : 'ready_to_close'}
                  aria-pressed={ready}
                  disabled={busy || !allowed}
                  onClick={() => {
                    if (!allowed) return;
                    trackEvent('action', 'qcp_case_ready_to_close', {
                      investigation_no: investigation.investigation_no,
                      investigation_id: investigation.id,
                      to_state: to,
                    });
                    void runCaseAction(
                      'court',
                      ready ? 'take back "ready to close"' : 'mark the case ready to close',
                      () => setInvestigationWorkState(investigation.id, to),
                    );
                  }}
                >
                  {/* Plain, not green, once it is set (UX round 2026-10-27:
                      the green "Not ready to close after all" read as a
                      success message). */}
                  {ready ? 'Take back' : '🏁 Mark ready to close'}
                </button>
                {closePath && (
                  <p className={styles.closePath} data-testid="case-close-path">
                    {closePath}
                  </p>
                )}
              </>
            );
          })()}
          {failedAt('court')}
        </section>
      )}

      <section className={styles.section}>
        <h4 className={styles.sectionHead}>
          Outstanding items
          {checklist.length > 0 &&
            ` — ${checklist.filter((t) => t.status === 'open').length} open`}
        </h4>
        {checklist.length === 0 && (
          <p className={styles.hint}>
            {active
              ? 'Nothing tracked yet — list the steps still standing between this case and closed, and whose move each one is.'
              : 'No outstanding items were tracked on this case.'}
          </p>
        )}
        {checklist.length > 0 && (
          <ul className={styles.taskList}>
            {checklist.map((task: DoorCaseTaskRead) => {
              const toggleTarget = taskToggleTarget(task);
              const otherDesk: CaseTaskOwner = nextTaskOwner(task.owner);
              const askGroups = taskAskGroups(task, asks);
              const askDesk = taskAskDesk(task, taskDesks?.[task.id]);
              const canAsk = canEdit && !!onAskTask && taskCanAsk(task, askGroups);
              return (
                <li
                  key={task.id}
                  className={styles.taskRow}
                  data-task-status={task.status}
                  // Recorder: every control in the row inherits the case and
                  // item ids from here; the controls themselves carry a family
                  // name so the item's wording ("clear SHERW01 credit hold …")
                  // never becomes an action name (kanban c2632a0b).
                  data-target-case-id={investigation.investigation_no}
                  data-target-task-id={task.id}
                >
                  <input
                    type="checkbox"
                    className={styles.taskCheckbox}
                    checked={task.status === 'done'}
                    disabled={busy || !canEdit || toggleTarget === null}
                    aria-label={`${task.status === 'done' ? 'Uncheck' : 'Check off'}: ${task.label}`}
                    // The name carries the direction: React restores the
                    // controlled box before the change event, so the recorded
                    // value is the state BEFORE the click (was_done).
                    data-action={task.status === 'done' ? 'qcp_case_item_untick' : 'qcp_case_item_tick'}
                    data-record-value="was_done"
                    onChange={() => {
                      if (toggleTarget === null) return;
                      void runCaseAction(
                        `task:${task.id}`,
                        `${toggleTarget === 'done' ? 'tick off' : 'untick'} ${quoted(task.label)}`,
                        () => transitionInvestigationTask(investigation.id, task.id, toggleTarget),
                      );
                    }}
                  />
                  <span className={styles.taskLabel}>{task.label}</span>
                  {taskClosedFromChatLine(task) && (
                    <span
                      className={styles.taskClosedNote}
                      data-testid="task-closed-from-chat"
                      title="The user told Merlin this was settled; Merlin checked it off and kept what was said"
                    >
                      {taskClosedFromChatLine(task)}
                    </span>
                  )}
                  {taskAssignmentLine(task) && (
                    <span
                      className={styles.taskAssignment}
                      data-overdue={task.overdue ? 'true' : undefined}
                      data-testid="task-assignment"
                      title={
                        task.overdue
                          ? 'Past the date the owner committed to'
                          : 'Who owns this step and by when (Sigma: a corrective action has an owner and a date)'
                      }
                    >
                      {taskAssignmentLine(task)}
                    </span>
                  )}
                  {canEdit && task.status === 'open' && assigningTask !== task.id && (
                    <button
                      type="button"
                      className={styles.taskAssignBtn}
                      disabled={busy}
                      aria-label={`${taskAssignmentLine(task) ? 'Change' : 'Set'} owner and date: ${task.label}`}
                      title="Name who owns this step and the date they committed to"
                      data-action="qcp_case_item_assign_open"
                      data-outcome="open"
                      onClick={() => openAssign(task)}
                    >
                      {taskAssignmentLine(task) ? 'edit who/when' : '+ who/when'}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.taskOwner}
                    data-owner={task.owner}
                    disabled={busy || !canEdit || task.status === 'done'}
                    title={taskOwnerHint(task.owner)}
                    data-action="qcp_case_item_owner_toggle"
                    data-record-value="owner"
                    value={task.owner}
                    onClick={() =>
                      void runCaseAction(
                        `task:${task.id}`,
                        taskOwnerAction(quoted(task.label), otherDesk),
                        () =>
                          editInvestigationTask(investigation.id, task.id, {
                            owner: otherDesk,
                          }),
                      )
                    }
                  >
                    {TASK_OWNER_LABEL[task.owner]}
                  </button>
                  {(askGroups.length > 0 || canAsk) && (
                    <span className={styles.taskAsks} data-testid="task-asks">
                      {askGroups.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          className={styles.taskAskPill}
                          data-state={g.state}
                          data-action="qcp_case_task_ask_pill"
                          data-target-ask-group-id={g.id}
                          title={`${g.title} (to ${g.recipients.map((r) => r.name).join(', ')}) — see it under Asks on this case`}
                          onClick={() => {
                            trackEvent('action', 'qcp_case_task_ask_pill', {
                              investigation_no: investigation.investigation_no,
                              task_id: task.id,
                              ask_group_id: g.id,
                              state: g.state,
                            });
                            document
                              .getElementById('case-asks')
                              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                        >
                          <span className={styles.taskAskDot} aria-hidden="true" />
                          {pillLabel(g)}
                          {g.unseen && <span className={styles.taskAskNew}>new</span>}
                        </button>
                      ))}
                      {canAsk && onAskTask && (
                        <button
                          type="button"
                          className={styles.taskAsk}
                          disabled={busy}
                          aria-label={`${askDesk.label}: ${task.label}`}
                          title="Send this item as a request — they reply here"
                          data-action="qcp_case_item_ask_open"
                          data-outcome="open"
                          onClick={() => onAskTask(task)}
                          data-shortcut="a"
                          data-shortcut-name="ask_colleague"
                        >
                          {askDesk.label}
                        </button>
                      )}
                    </span>
                  )}
                  {isCustomerStep(task) && task.status === 'open' && (
                    // The customer's move is never asked from here (Jess,
                    // 2026-10-21): the row recommends the route — through
                    // the account's sales rep — and Jess or Merlin reword it.
                    <span
                      className={styles.taskRecommend}
                      data-testid="task-recommend"
                      data-own={hasOwnRecommendation(task) ? 'true' : undefined}
                      title={CUSTOMER_STEP_HINT}
                    >
                      <span className={styles.taskRecommendTag}>Recommend</span>
                      <span className={styles.taskRecommendText}>
                        {customerRecommendation(task, customerRelay)}
                      </span>
                      {canEdit && recommendingTask !== task.id && (
                        <button
                          type="button"
                          className={styles.taskAssignBtn}
                          disabled={busy}
                          aria-label={`Reword the recommendation: ${task.label}`}
                          title="Say how this reaches the customer (through the sales rep); Merlin can reword it from chat too"
                          data-action="qcp_case_item_recommend_edit_open"
                          data-outcome="open"
                          onClick={() => openRecommend(task)}
                        >
                          reword
                        </button>
                      )}
                    </span>
                  )}
                  {recommendingTask === task.id && (
                    <span className={styles.taskAssignRow} role="group" aria-label={`Recommendation: ${task.label}`}>
                      <input
                        className={styles.textInput}
                        value={recommendDraft}
                        onChange={(e) => setRecommendDraft(e.target.value)}
                        placeholder={defaultCustomerRecommendation(customerRelay)}
                        aria-label={`Recommendation text: ${task.label}`}
                        disabled={busy}
                        maxLength={1000}
                      />
                      <button
                        type="button"
                        className={styles.inlineConfirmYes}
                        disabled={busy}
                        data-action="qcp_case_item_recommend_save"
                        data-outcome="request"
                        onClick={() => saveRecommend(task)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className={styles.inlineConfirmNo}
                        data-action="qcp_case_item_recommend_cancel"
                        data-outcome="close"
                        onClick={() => setRecommendingTask(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  )}
                  {canEdit && task.allowed_statuses.includes('cancelled') && (
                    // Remove: one click, or with a note Merlin learns from
                    // (kanban 59a1ee04). The item folds into "Dismissed"
                    // below, never deleted.
                    <DismissControl
                      what={task.label}
                      label="Remove"
                      noteLabel="say why"
                      placeholder="Why? e.g. already done, answered in the orders review meeting"
                      size="row"
                      danger
                      showError={false}
                      disabled={busy}
                      onDismiss={(note) => removeTask(task, note)}
                      track={{
                        plain: 'qcp_case_item_dismiss',
                        note: 'qcp_case_item_dismiss_note',
                        form: 'qcp_case_item_reason',
                        targets: { case_id: investigation.investigation_no, task_id: task.id },
                        context: { investigation_no: investigation.investigation_no, task_id: task.id },
                      }}
                    />
                  )}
                  {assigningTask === task.id && (
                    <span className={styles.taskAssignRow} role="group" aria-label={`Owner and date: ${task.label}`}>
                      <input
                        className={styles.textInput}
                        value={assignName}
                        onChange={(e) => setAssignName(e.target.value)}
                        placeholder="Owner (name)"
                        aria-label={`Owner name: ${task.label}`}
                        disabled={busy}
                      />
                      <input
                        type="date"
                        className={styles.taskDateInput}
                        value={assignDate}
                        onChange={(e) => setAssignDate(e.target.value)}
                        aria-label={`Completion date: ${task.label}`}
                        disabled={busy}
                      />
                      <button
                        type="button"
                        className={styles.inlineConfirmYes}
                        disabled={busy}
                        onClick={() => saveAssign(task)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className={styles.inlineConfirmNo}
                        onClick={() => setAssigningTask(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  )}
                  {failedAt(`task:${task.id}`)}
                </li>
              );
            })}
          </ul>
        )}
        {dismissedTasks.length > 0 && (
          <div className={styles.dismissedFold} data-testid="dismissed-items">
            <button
              type="button"
              className={styles.dismissedToggle}
              onClick={() => setShowDismissed((v) => !v)}
              aria-expanded={showDismissed}
            >
              {showDismissed ? 'Hide' : 'Show'} {dismissedTasks.length} dismissed
            </button>
            {showDismissed && (
              <ul className={styles.taskList}>
                {dismissedTasks.map((task) => (
                  <li key={task.id} className={styles.taskRow} data-task-status="cancelled" data-testid="dismissed-item">
                    <span className={styles.taskLabel}>{task.label}</span>
                    {task.cancelled_note && (
                      <span className={styles.taskAssignment} data-testid="dismissed-note">
                        &ldquo;{task.cancelled_note}&rdquo;
                      </span>
                    )}
                    {canEdit && task.allowed_statuses.includes('open') && (
                      <button
                        type="button"
                        className={styles.taskAssignBtn}
                        disabled={busy}
                        aria-label={`Restore: ${task.label}`}
                        title="Put it back on the checklist"
                        onClick={() =>
                          void runCaseAction(`task:${task.id}`, `restore ${quoted(task.label)}`, () =>
                            transitionInvestigationTask(investigation.id, task.id, 'open'),
                          )
                        }
                      >
                        restore
                      </button>
                    )}
                    {failedAt(`task:${task.id}`)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {canEdit && (
          <div className={styles.taskAddRow}>
            <input
              className={styles.textInput}
              value={newTaskLabel}
              onChange={(e) => setNewTaskLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTask();
                }
              }}
              placeholder="Add an outstanding step…"
              aria-label="New outstanding item"
              disabled={busy}
            />
            <select
              className={styles.taskOwnerSelect}
              value={newTaskOwner}
              onChange={(e) =>
                setNewTaskOwner(e.target.value as CaseTaskOwner)
              }
              aria-label="Whose move is it?"
              disabled={busy}
            >
              <option value="us">on us</option>
              <option value="colleague">on a colleague</option>
              <option value="customer">on customer</option>
            </select>
            <input
              className={`${styles.textInput} ${styles.taskOwnerName}`}
              value={newTaskOwnerName}
              onChange={(e) => setNewTaskOwnerName(e.target.value)}
              placeholder="Owner (name, optional)"
              aria-label="New item owner name"
              title="Sigma: a corrective action has an owner"
              disabled={busy}
            />
            <input
              type="date"
              className={styles.taskDateInput}
              value={newTaskDueAt}
              onChange={(e) => setNewTaskDueAt(e.target.value)}
              aria-label="New item completion date"
              title="Sigma: a corrective action has a completion date"
              disabled={busy}
            />
            <button
              type="button"
              className={styles.taskAddBtn}
              onClick={addTask}
              disabled={busy || !newTaskLabel.trim()}
            >
              Add
            </button>
          </div>
        )}
        {failedAt('addTask')}
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionHead}>Findings</h4>
        {findingsEditable ? (
          <>
          <textarea
            className={styles.textInput}
            value={findingsDraft}
            onChange={(e) => setFindingsDraft(e.target.value)}
            placeholder="What did the investigation turn up? Required before moving to Remediating."
            // Tall enough to read without scrolling inside a 4-line box.
            rows={textRows(findingsDraft)}
            aria-label="Findings"
            disabled={busy}
          />
          {/* Not a quiet grey hint any more (case round, finding 3): text
              typed here is NOT on the case until the stage moves. */}
          {findingsDraft.trim() !== investigation.findings.trim() && (
            <div className={styles.draftWarning} role="status" data-testid="findings-draft-warning">
              <strong>Not saved to the case yet.</strong> Only you see this draft, on this
              computer, until you move the case to Remediating. Then it is saved to the case
              for everyone.
            </div>
          )}
          </>
        ) : (
          <p className={styles.readonlyText}>
            {investigation.findings || '—'}
          </p>
        )}
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionHead}>Remediation actions</h4>
        {remediationEditable ? (
          <>
            <textarea
              className={styles.textInput}
              value={remediationDraft}
              onChange={(e) => setRemediationDraft(e.target.value)}
              placeholder={'One action per line, e.g.\nRe-blend lot 4471-B\nRetrain the packaging crew'}
              rows={4}
              aria-label="Remediation actions"
              disabled={busy}
            />
            <p className={styles.hint}>
              One action per line — required before marking Completed.
            </p>
          </>
        ) : investigation.remediation_details.length > 0 ? (
          <ul className={styles.remediationList}>
            {investigation.remediation_details.map((item, i) => (
              <li key={`${i}-${item}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className={styles.readonlyText}>—</p>
        )}
      </section>

      {investigation.status !== 'cancelled' && (
        <section className={styles.section} aria-label="Customer response" data-testid="customer-response">
          <h4 className={styles.sectionHead}>Customer response</h4>
          {customerResponseLine(investigation) ? (
            <>
              <p className={styles.customerResponseSent} data-testid="customer-response-sent">
                ✓ Sent {formatInvestigationDate(investigation.customer_response_sent_at ?? '')}
              </p>
              {investigation.customer_response_summary && (
                <p className={styles.customerResponseText}>{investigation.customer_response_summary}</p>
              )}
            </>
          ) : (
            <p className={styles.hint}>
              Sigma stage 4: close the loop with the customer in writing (the clock above shows
              when it is due). Say what they were told, then mark it sent — the response itself
              goes out by email or phone, through the account's sales rep.
            </p>
          )}
          {!readOnly && (<div className={styles.customerResponseRow}>
            <textarea
              className={styles.textInput}
              rows={2}
              value={customerResponseDraft}
              onChange={(e) => setCustomerResponseDraft(e.target.value)}
              placeholder={
                customerResponseLine(investigation)
                  ? 'Record a further response…'
                  : 'What the customer was told, in a sentence or two…'
              }
              aria-label="Customer response summary"
              disabled={busy}
            />
            <button
              type="button"
              className={styles.taskAddBtn}
              onClick={markCustomerResponseSent}
              disabled={busy || !customerResponseDraft.trim()}
            >
              Mark as sent
            </button>
          </div>)}
          {failedAt('customerResponse')}
        </section>
      )}

      {investigation.status === 'completed' && (
        <section className={styles.section}>
          {/* An empty "Resolution —" under filled-in findings and actions
              read as something missing; show the heading only with text. */}
          {investigation.resolution_summary ? (
            <>
              <h4 className={styles.sectionHead}>Resolution</h4>
              <p className={styles.readonlyText}>{investigation.resolution_summary}</p>
            </>
          ) : null}
          {investigation.completed_at && (
            <p className={styles.hint}>
              Completed {formatInvestigationDate(investigation.completed_at)}
            </p>
          )}
          {stillOpenElsewhere && (
            // The case is done here; the sales-site complaint is not ticked
            // there and the portal cannot tick it yet. Say so, by number, so
            // nobody has to go hunting (Jess, QT-10021, 2026-09-14).
            <div
              className={styles.externalClosureNotice}
              role="status"
              data-testid="external-closure-notice"
            >
              <strong>{stillOpenElsewhere.issueNos.join(', ')}</strong>
              {': '}
              {stillOpenElsewhere.text}
            </div>
          )}
        </section>
      )}

      <section className={styles.section} aria-label="Packaging feedback">
        <h4 className={styles.sectionHead}>Packaging feedback (paste into the sales site)</h4>
        {feedback === null ? (
          <>
            <p className={styles.hint}>
              Lays this case out in the sales-site complaint form&apos;s sections so
              each one can be copied and pasted. Nothing is written to the sales site.
            </p>
            <button
              type="button"
              className={styles.taskAddBtn}
              onClick={loadFeedback}
              disabled={feedbackLoading}
            >
              {feedbackLoading ? 'Preparing…' : 'Prepare packaging feedback'}
            </button>
          </>
        ) : (
          <>
            <p className={styles.hint}>
              {feedback.issue_no
                ? `For sales-site complaint ${feedback.issue_no}. `
                : 'No sales-site complaint is linked to this case. '}
              Copy a section, then paste it into the matching field on the sales site.
            </p>
            <div className={styles.packagingActions}>
              <button
                type="button"
                className={styles.taskAddBtn}
                onClick={() => copySection('__all__', feedback.paste_all)}
                disabled={!feedback.paste_all}
              >
                {copiedKey === '__all__' ? 'Copied all ✓' : 'Copy all filled sections'}
              </button>
              <button
                type="button"
                className={styles.taskAddBtn}
                onClick={loadFeedback}
                disabled={feedbackLoading}
              >
                {feedbackLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            <dl className={styles.complaintGrid}>
              {feedback.sections.map((s) => {
                const source = sourceHint(s);
                const choices = choicesHint(s);
                return (
                  <div
                    key={s.key}
                    className={styles.complaintField}
                    data-testid={`packaging-${s.key}`}
                  >
                    <dt className={styles.packagingLabel}>
                      {s.label}
                      <button
                        type="button"
                        className={styles.copyBtn}
                        aria-label={`Copy ${s.label}`}
                        disabled={!s.value.trim()}
                        onClick={() => copySection(s.key, sectionClipboardText(s))}
                      >
                        {copiedKey === s.key ? 'Copied ✓' : 'Copy'}
                      </button>
                    </dt>
                    <dd>
                      {s.value.trim() ? (
                        <pre className={styles.packagingValue}>{s.value}</pre>
                      ) : (
                        <div className={styles.readonlyText}>— fill in on the form</div>
                      )}
                      {source && <div className={styles.packagingHint}>{source}</div>}
                      {choices && <div className={styles.packagingHint}>{choices}</div>}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </>
        )}
        {feedbackError && (
          <p className={styles.hint} role="alert">
            {feedbackError}
          </p>
        )}
      </section>

      {historyWindow && (historyWindow.events.length > 0 || historyWindow.has_more) && (
        <section className={styles.section}>
          {/* One name for an item's and a case's record of what happened
              (UX round 2026-11-03, merge): "Timeline", as on the item. */}
          <h4 className={styles.sectionHead}>Timeline · newest first</h4>
          <div className={styles.timeline} aria-label="Case timeline">
            {collapseRepeats(
              // Checklist lines name their item when the case's checklist
              // shows which one changed at that moment (case round, finding 13).
              historyWindow.events.map((e) => ({ ...e, summary: caseEventSummary(e, investigation.tasks) })),
            ).map(({ ev, times }, i) => (
              <div
                key={`${ev.event_type}-${i}`}
                className={styles.timelineRow}
                data-event={ev.event_type}
              >
                <span
                  className={styles.timelineDot}
                  data-actor={ev.actor_kind}
                  aria-hidden="true"
                />
                <span className={styles.timelineDate}>
                  {formatInvestigationDate(ev.created_at)}
                </span>
                <span className={styles.timelineText}>
                  <span className={styles.timelineWho}>{ev.actor_label || actorWord(ev.actor_kind)}</span>
                  {ev.summary}
                  {times > 1 ? ` (×${times})` : ''}
                </span>
              </div>
            ))}
          </div>
          {historyWindow.has_more && (
            <button
              type="button"
              className={styles.openConversationBtn}
              onClick={history.showOlder}
              disabled={history.loadingOlder}
            >
              {history.loadingOlder ? 'Loading older timeline…' : 'Show older'}
            </button>
          )}
          {history.olderError && <FailNote message={history.olderError} />}
        </section>
      )}


      {pending && (
        <div className={styles.pendingStep} role="group" aria-label="Confirm this step">
          {pending.needs === 'reopen_note' ? (
            <>
              <label className={styles.pendingHead} htmlFor="reopen-note">
                What came back? (optional)
              </label>
              <textarea
                id="reopen-note"
                className={styles.textInput}
                rows={2}
                value={reopenNote}
                onChange={(e) => setReopenNote(e.target.value)}
                placeholder="e.g. two more drums skinned over on the same lot"
              />
            </>
          ) : pending.to === 'cancelled' ? (
            <p className={styles.pendingHead}>
              Cancel {investigation.investigation_no}? It keeps its history but leaves the
              active list.
            </p>
          ) : (
            <p className={styles.pendingHead}>
              {openItemCount > 0 &&
                `${openItemCount} outstanding item${openItemCount === 1 ? ' is' : 's are'} still open. `}
              {noKeys &&
                'It has no keys (customer, product, lot), so a search in Done will not find it. '}
              Complete {investigation.investigation_no} anyway?
            </p>
          )}
          <div className={styles.pendingBtns}>
            <button
              type="button"
              className={buttonClass(pending.kind)}
              disabled={busy}
              onClick={() => void run(pending, true)}
            >
              {pending.to === 'cancelled'
                ? 'Yes, cancel it'
                : pending.needs === 'reopen_note'
                  ? 'Reopen'
                  : 'Complete anyway'}
            </button>
            <button
              type="button"
              className={buttonClass('ghost')}
              onClick={() => {
                setPending(null);
                setFailure(null);
              }}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {specs.length > 0 && !readOnly && (
        <div className={styles.transitions}>
          {/* Inside the (pinned, when popped out) stage bar, so the refusal
              is always beside the button that was pressed. */}
          {failedAt('stage')}
          {specs.map((spec) => (
            <button
              key={`${spec.to}-${spec.label}`}
              type="button"
              className={buttonClass(spec.kind)}
              onClick={() => void run(spec)}
              disabled={busy}
            >
              {spec.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default InvestigationCaseFile;
